/**
 * [TD-47.2b] SnakeDraftRoom - orchestrator now consuming UnifiedDraftShell.
 *
 * Replaces the prior 402-line standalone shell with the unified shell from
 * TD-47.2a + render-prop slots filled with snake-specific content. Founder
 * verdicts locked in v1.2-TD-47.2b:
 *   • Gap A.1 - submitPick lives on SnakeDraftContext (REST /submit-pick
 *               with request_id idempotency); SnakePlayerPool consumes it.
 *   • Gap B.1 - Autopick double-press lives in SnakeCenterSurface YOUR
 *               TURN cinematic, beside the timer.
 *   • Gap D.1 - Right-pane tab order Activity → Chat → Pick History.
 *   • Gap E.3 - Mobile mounts SnakeCenterSurface + SnakePlayerPool inline;
 *               PickHistorySidebar stays in the mobile flow; tab-switcher
 *               gains Pick History parity with desktop.
 *   • Gap F.2 - DraftFiltersProvider wraps the inner so RosterPanel YOU
 *               NEED chips publish to PlayerPoolWithDrawer's posFilter.
 *   • Gap G.1 - OnDeckIndicator relocated into SnakeCenterSurface scouting
 *               mode header; null-return collapses naturally.
 *   • Gap H.1 - Left-pane RoundProgressBar duplicate deleted; top-bar copy
 *               kept; mobile keeps its inline copy.
 *
 * Retired in this delivery: OnTheClockPanel.jsx + PlayerSearchAndPick.jsx
 * (content folded into SnakeCenterSurface + SnakePlayerPool).
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { Activity as ActivityIcon, MessageSquare, History as HistoryIcon, ListChecks, Users as UsersIcon } from "lucide-react";
import { useAuth, authHeaders } from "../../contexts/AuthContext";
import { SnakeDraftProvider, useSnakeDraft } from "../../contexts/SnakeDraftContext";
import { DraftFiltersProvider } from "../../contexts/DraftFiltersContext";
import { DraftQueueProvider } from "../../contexts/DraftQueueContext";
import { AutoDraftProvider } from "../../contexts/AutoDraftContext";
import { useTheme } from "../../contexts/ThemeContext";
import { setAudioVolume } from "../../audio/auctioneerAudio";
import { useAdamVoiceGate } from "../../hooks/useAdamVoiceGate";

import UnifiedDraftShell from "../draft/UnifiedDraftShell";
import SnakePlayerPool from "../draft/SnakePlayerPool";
import SnakeCenterSurface from "./SnakeCenterSurface";
import RosterPanel from "../draft/RosterPanel";
import TabbedPanel from "../draft/TabbedPanel";
import LiveActivityFeed from "../activity/LiveActivityFeed";
import ChatPanelWithFilter from "../draft/ChatPanelWithFilter";
import PlayerAnalysisCanvas from "../draft/PlayerAnalysisCanvas";
import PickHistoryTabBody from "../draft/PickHistoryTabBody";
import RoundProgressBar from "./RoundProgressBar";
import CommissionerControls from "../draft/CommissionerControls";
import DraftRoomAbandonButton from "../draft/DraftRoomAbandonButton";
import CommissionerManagersDropdown from "../draft/CommissionerManagersDropdown";
import DraftQueue from "../draft/DraftQueue";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SnakeDraftRoomInner({ leagueId, league }) {
  const { user } = useAuth();
  const {
    draftState, isConnected, activityVoice, bridgeRecapUrl,
    mockDraftCompleteEvent, setMockDraftCompleteEvent,
    latestAdamMessage,
    recapDismissed, dismissRecap,
  } = useSnakeDraft();
  const { isDark } = useTheme();

  const isCommissioner = league?.commissioner_id === user?.id;
  const draftIsComplete = draftState?.state === "DRAFT_COMPLETE";

  // [TD-47.6a-ui v1.3 Redline 3.a] Canvas player state - snake now hosts the
  // PlayerAnalysisCanvas in the center-middle slot, fed by pool-row clicks
  // via SnakePlayerPool's onCanvasUpdate callback (same triple-callback
  // pattern auction's DraftRoom uses).
  const [canvasPlayer, setCanvasPlayer] = useState(null);

  // [HF2-7 v1.2 + A.1.8] Sound mute + volume persisted to localStorage. Default ON.
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem("pgf_audio_muted") === "1"; } catch { return false; }
  });
  const [volume, setVolume] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem("pgf_audio_volume") || "80", 10);
      return Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : 80;
    } catch { return 80; }
  });
  useEffect(() => {
    try { localStorage.setItem("pgf_audio_muted", muted ? "1" : "0"); } catch { /* ignore */ }
    try { localStorage.setItem("pgf_audio_volume", String(volume)); } catch { /* ignore */ }
    setAudioVolume(muted ? 0 : volume / 100);
  }, [muted, volume]);
  const toggleMute = () => setMuted((prev) => !prev);
  const handleVolumeChange = (e) => {
    const v = parseInt(e.target.value, 10);
    setVolume(v);
    if (v === 0 && !muted) setMuted(true);
    if (v > 0 && muted) setMuted(false);
  };
  const audioControls = { muted, volume, toggleMute, handleVolumeChange };

  // [ADAM AUDIO foundation] Adam lines are enqueued by SnakeDraftContext
  // straight into the audio singleton; this only syncs the mute gate.
  useAdamVoiceGate(muted);

  // Top-bar center: round progress bar (snake-specific).
  const renderTopBarCenter = () => (
    draftState ? (
      <div className="max-w-md mx-auto">
        <RoundProgressBar draftState={draftState} isDark={isDark} />
      </div>
    ) : null
  );

  // [TD-47.5 Gap 4.A] Right-top "your data" tabs - Roster default, Queue,
  // History. Activity + Chat moved to permanent center-bottom + right-bottom
  // mounts (no longer compete for one slot). LiveActivityFeed-as-tab gone.
  const rightTopTabs = [
    {
      id: "roster",
      label: "Roster",
      icon: UsersIcon,
      render: () => (
        <RosterPanel
          draftState={draftState}
          userId={user?.id}
          testIdPrefix="snake-roster-panel"
        />
      ),
    },
    {
      id: "queue",
      label: "Queue",
      icon: ListChecks,
      render: () => <DraftQueue />,
    },
    {
      id: "history",
      label: "History",
      icon: HistoryIcon,
      render: () => (
        <PickHistoryTabBody
          draftState={draftState}
          activityVoice={activityVoice}
          isDark={isDark}
        />
      ),
    },
  ];

  // Mobile fallback (Gap E.3 - minimum delta). Mobile keeps the full
  // tab-switcher because there's no vertical room for the desktop split;
  // founder TD-10 will rebuild mobile from scratch in Phase 2e.
  const mobileTabs = [
    {
      id: "activity",
      label: "Activity",
      icon: ActivityIcon,
      render: () => (
        <LiveActivityFeed
          activityVoice={activityVoice}
          isDark={isDark}
          format="snake"
          showSpeakerIcons={false}
          audioControls={audioControls}
        />
      ),
    },
    { id: "chat", label: "Chat", icon: MessageSquare, render: () => (
      <ChatPanelWithFilter dataHook={useSnakeDraft} audioControls={audioControls} />
    ) },
    {
      id: "history",
      label: "History",
      icon: HistoryIcon,
      render: () => (
        <PickHistoryTabBody draftState={draftState} activityVoice={activityVoice} isDark={isDark} />
      ),
    },
    {
      id: "queue",
      label: "Queue",
      icon: ListChecks,
      render: () => <DraftQueue />,
    },
  ];

  const renderMobile = () => (
    <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-3">
      {draftState && (
        <div
          className="rounded-2xl border p-3"
          style={{
            background: isDark ? "#1C1C1C" : "#FFFFFF",
            borderColor: isDark ? "#252525" : "#D8D8D8",
          }}
        >
          <RoundProgressBar draftState={draftState} isDark={isDark} />
        </div>
      )}
      {/* [P1 reconnect] PAUSED must keep the surface mounted on mobile too,
          otherwise a paused draft looks like an empty room. */}
      {(draftState?.state === "DRAFTING" || draftState?.state === "PAUSED") && (
        <SnakeCenterSurface draftState={draftState} leagueId={leagueId} isDark={isDark} />
      )}
      <SnakePlayerPool draftState={draftState} leagueId={leagueId} league={league} />
      <div className="h-72">
        <TabbedPanel testIdPrefix="snake-mobile-tabs" defaultTab="activity" tabs={mobileTabs} />
      </div>
    </div>
  );

  return (
    <>
      <Toaster
        richColors
        position="bottom-right"
        toastOptions={{ className: "font-body text-xs" }}
      />
      <UnifiedDraftShell
      format="snake"
      leagueId={leagueId}
      league={league}
      isConnected={isConnected}
      draftIsComplete={draftIsComplete}
      bridgeRecapUrl={bridgeRecapUrl}
      recapDismissed={recapDismissed}
      dismissRecap={dismissRecap}
      mockDraftCompleteEvent={mockDraftCompleteEvent}
      setMockDraftCompleteEvent={setMockDraftCompleteEvent}
      isCommissioner={isCommissioner}
      boardUrl={`/draft-snake/${leagueId}/board`}
      formatBadgeText="Snake"
      renderTopBarCenter={renderTopBarCenter}
      renderCommissionerControls={() =>
        isCommissioner && draftState ? (
          <div className="flex items-stretch">
            <CommissionerControls leagueId={leagueId} draftState={draftState} league={league} />
            <DraftRoomAbandonButton league={league} isCommissioner={isCommissioner} isDark={isDark} />
            <CommissionerManagersDropdown league={league} isCommissioner={isCommissioner} isDark={isDark} />
          </div>
        ) : null
      }
      renderLeftPane={() => (
        <SnakePlayerPool
          draftState={draftState}
          leagueId={leagueId}
          onCanvasUpdate={setCanvasPlayer}
          leagueRoster={league?.settings?.roster}
          league={league}
        />
      )}
      renderCenterTop={() => (
        <SnakeCenterSurface draftState={draftState} leagueId={leagueId} isDark={isDark} />
      )}
      renderCenterMiddle={() => (
        <PlayerAnalysisCanvas leagueId={leagueId} currentPlayer={canvasPlayer} />
      )}
      renderRightTop={() => (
        <TabbedPanel
          testIdPrefix="snake-right-top-tabs"
          defaultTab="roster"
          tabs={rightTopTabs}
        />
      )}
      /* [TD-47.5 Gap 12.A] Snake has no AI/Curly middle section - slot omitted */
      /* [TD-47.6a-ui v1.3 Redline 3.b/3.f] Right-bottom now mounts the
         unified ChatPanelWithFilter (merged chat + Adam stream) with snake's
         data-hook. Desktop LiveActivityFeed mount retired; mobile keeps its
         own LiveActivityFeed in renderMobile per founder direction. */
      renderRightBottom={() => (
        <ChatPanelWithFilter dataHook={useSnakeDraft} audioControls={audioControls} />
      )}
      renderMobile={renderMobile}
      />
    </>
  );
}

export default function SnakeDraftRoom() {
  const { id: leagueId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/leagues/${leagueId}`, { headers: authHeaders() })
      .then((r) => {
        setLeague(r.data);
        if (r.data.status === "SETUP") nav(`/league/${leagueId}/setup`);
        else if (r.data.status === "LOBBY") nav(`/league/${leagueId}/lobby`);
      })
      .catch(() => nav("/dashboard"))
      .finally(() => setLoading(false));
  }, [leagueId, nav]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF6B00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!league) return null;

  return (
    <SnakeDraftProvider leagueId={leagueId}>
      <DraftFiltersProvider>
        <DraftQueueProvider leagueId={leagueId} userId={user?.id}>
          <AutoDraftProvider leagueId={leagueId} userId={user?.id}>
            <SnakeDraftRoomInner leagueId={leagueId} league={league} />
          </AutoDraftProvider>
        </DraftQueueProvider>
      </DraftFiltersProvider>
    </SnakeDraftProvider>
  );
}
